<template>
  <v-container fluid class="pa-4">
    <!-- Header Principal -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-2">Auditoría de Inventario</h1>
        <p class="text-subtitle-1 grey--text">
          Sistema de auditoría offline con gestión por lotes
        </p>
      </v-col>
    </v-row>

    <!-- Estados de Sincronización -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-alert :type="syncStatus.type" dense outlined class="mb-0">
          <div class="d-flex align-center">
            <v-icon left>{{ syncStatus.icon }}</v-icon>
            <span>{{ syncStatus.message }}</span>
            <v-spacer></v-spacer>
            <span class="text-caption">
              Última sincronización: {{ lastSyncFormatted }}
            </span>
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- KPIs de Auditoría -->
    <v-row dense class="mb-4">
      <v-col v-for="kpi in auditKpis" :key="kpi.key" cols="6" sm="3" md="2">
        <v-card outlined class="pa-3">
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <div class="text-caption grey--text">{{ kpi.label }}</div>
              <div class="text-h6 font-weight-bold">{{ kpi.value }}</div>
              <div class="text-caption">{{ kpi.subtitle }}</div>
            </div>
            <v-icon :color="kpi.color" size="24">{{ kpi.icon }}</v-icon>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs Principales -->
    <v-card>
      <v-tabs v-model="activeTab" color="primary">
        <v-tab>
          <v-icon left>mdi-cloud-download</v-icon>
          Sincronización
        </v-tab>
        <v-tab :disabled="!hasLocalInventory">
          <v-icon left>mdi-package-variant-closed</v-icon>
          Gestión de Lotes
        </v-tab>
        <v-tab :disabled="!hasActiveBatch">
          <v-icon left>mdi-barcode-scan</v-icon>
          Conteo
        </v-tab>
        <v-tab :disabled="!hasAuditData">
          <v-icon left>mdi-chart-box</v-icon>
          Resultados
        </v-tab>
        <v-tab :disabled="!canCloseAudit">
          <v-icon left>mdi-clipboard-check</v-icon>
          Cierre
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Tab 1: Sincronización -->
        <v-tab-item>
          <v-card-text>
            <!-- Resumen de Valores -->
            <v-row class="mb-4">
              <v-col cols="12">
                <v-card outlined class="pa-4">
                  <h3 class="text-h6 mb-3">
                    <v-icon left>mdi-calculator</v-icon>
                    Resumen de Inventario Local
                  </h3>
                  <v-row dense>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h5 font-weight-bold primary--text">
                          {{ localInventoryCount.toLocaleString() }}
                        </div>
                        <div class="text-caption grey--text">Productos</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h5 font-weight-bold success--text">
                          ${{ formatCurrency(totalInventoryCost) }}
                        </div>
                        <div class="text-caption grey--text">Costo Total</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h5 font-weight-bold info--text">
                          ${{ formatCurrency(totalInventoryPrice) }}
                        </div>
                        <div class="text-caption grey--text">Precio Total</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h5 font-weight-bold warning--text">
                          ${{ formatCurrency(totalInventoryMargin) }}
                        </div>
                        <div class="text-caption grey--text">Margen Total</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4">
                  <h3 class="text-h6 mb-4">
                    <v-icon left>mdi-download</v-icon>
                    Descargar Inventario
                  </h3>

                  <v-alert v-if="!hasLocalInventory" type="info" dense class="mb-4">
                    No hay inventario descargado. Descarga el inventario actual para
                    comenzar.
                  </v-alert>

                  <v-alert v-else type="success" dense class="mb-4">
                    Inventario local: {{ localInventoryCount }} productos
                    <br />
                    <small>
                      Costo: ${{ formatCurrency(totalInventoryCost) }} | Precio: ${{
                        formatCurrency(totalInventoryPrice)
                      }}
                    </small>
                  </v-alert>

                  <v-progress-linear
                    v-if="downloading || downloadPhase === 'paused' || downloadPhase === 'error'"
                    :value="downloadProgress.percent"
                    height="22"
                    rounded
                    striped
                    :indeterminate="downloadPhase === 'counting'"
                    :color="
                      downloadPhase === 'error'
                        ? 'error'
                        : downloadPhase === 'paused'
                        ? 'warning'
                        : 'primary'
                    "
                    class="mb-2"
                  >
                    <strong>{{ downloadProgress.percent }}%</strong>
                  </v-progress-linear>

                  <div
                    v-if="downloading || downloadPhase === 'paused' || downloadPhase === 'error'"
                    class="text-caption text-center mb-1"
                  >
                    {{ downloadStatusText }}
                  </div>
                  <div
                    v-if="downloading"
                    class="text-caption text-center grey--text mb-2"
                  >
                    Tiempo transcurrido: {{ downloadElapsed }}s · Puedes cancelar y
                    reanudar después. No cierres esta pestaña.
                  </div>

                  <v-btn
                    v-if="downloading"
                    color="grey darken-1"
                    text
                    block
                    class="mb-2"
                    :disabled="cancelRequested"
                    @click="requestCancelDownload"
                  >
                    <v-icon left>mdi-close-circle-outline</v-icon>
                    {{ cancelRequested ? "Cancelando…" : "Cancelar descarga" }}
                  </v-btn>

                  <v-btn
                    color="primary"
                    block
                    @click="downloadInventory"
                    :loading="downloading"
                    :disabled="downloading"
                  >
                    <v-icon left>mdi-cloud-download</v-icon>
                    {{ downloadButtonLabel }}
                  </v-btn>

                  <v-divider class="my-4"></v-divider>

                  <div class="text-caption grey--text">
                    <p>
                      <strong>Nota:</strong> Esta acción descargará todo el inventario
                      actual de la tienda para trabajar sin conexión.
                    </p>
                    <p class="mb-0">Tamaño estimado: ~{{ estimatedSize }} MB</p>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined class="pa-4">
                  <h3 class="text-h6 mb-4">
                    <v-icon left>mdi-upload</v-icon>
                  </h3>

                  <!-- <v-alert v-if="!hasAuditData" type="warning" dense class="mb-4">
                    No hay datos de auditoría para sincronizar
                  </v-alert>

                  <v-alert v-else type="info" dense class="mb-4">
                    {{ pendingSyncCount }} registros pendientes de sincronización
                  </v-alert> -->
                  <!-- 
                  <v-btn
                    color="success"
                    block
                    @click="uploadResults"
                    :loading="uploading"
                    :disabled="!hasAuditData"
                  >
                    <v-icon left>mdi-cloud-upload</v-icon>
                    Sincronizar Resultados
                  </v-btn> -->

                  <v-divider class="my-4"></v-divider>

                  <v-btn
                    color="error"
                    text
                    block
                    @click="clearLocalData"
                    :disabled="!hasLocalInventory && !hasAuditData"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Limpiar Datos Locales
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- Tab 2: Gestión de Lotes -->
        <v-tab-item>
          <v-card-text>
            <!-- Crear Nuevo Lote -->
            <v-row>
              <v-col cols="12">
                <v-card outlined class="pa-4 mb-4">
                  <h3 class="text-h6 mb-4">Crear Nuevo Lote</h3>
                  <v-form ref="batchForm" v-model="batchFormValid">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="newBatch.name"
                          label="Nombre del Lote"
                          placeholder="Ej: Góndola 1, Pasillo 3, Bodega A"
                          outlined
                          dense
                          :rules="[(v) => !!v || 'El nombre es requerido']"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-textarea
                          v-model="newBatch.description"
                          label="Descripción (opcional)"
                          outlined
                          dense
                          rows="1"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12">
                        <v-btn
                          color="primary"
                          @click="createBatch"
                          :disabled="!batchFormValid"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Crear Lote
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card>
              </v-col>
            </v-row>

            <!-- Lista de Lotes -->
            <v-row>
              <v-col cols="12">
                <h3 class="text-h6 mb-4">Lotes de Auditoría</h3>
                <v-data-table
                  :headers="batchHeaders"
                  :items="batches"
                  :items-per-page="10"
                  class="elevation-1"
                >
                  <template v-slot:item.status="{ item }">
                    <v-chip :color="getBatchStatusColor(item.status)" small dark>
                      {{ getBatchStatusText(item.status) }}
                    </v-chip>
                  </template>

                  <template v-slot:item.itemCount="{ item }">
                    <div class="text-center">
                      <div class="font-weight-medium">{{ item.itemCount || 0 }}</div>
                      <div class="text-caption grey--text">items</div>
                    </div>
                  </template>

                  <template v-slot:item.totalCost="{ item }">
                    <div class="text-center">
                      <div class="font-weight-medium success--text">
                        ${{ formatCurrency(getBatchTotalCost(item)) }}
                      </div>
                      <div class="text-caption grey--text">costo</div>
                    </div>
                  </template>

                  <template v-slot:item.totalPrice="{ item }">
                    <div class="text-center">
                      <div class="font-weight-medium info--text">
                        ${{ formatCurrency(getBatchTotalPrice(item)) }}
                      </div>
                      <div class="text-caption grey--text">precio</div>
                    </div>
                  </template>

                  <template v-slot:item.createdAt="{ item }">
                    {{ formatDate(item.createdAt) }}
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      v-if="item.status === 'open'"
                      color="primary"
                      small
                      @click="selectBatch(item)"
                    >
                      <v-icon left small>mdi-barcode-scan</v-icon>
                      Continuar
                    </v-btn>
                    <v-btn
                      v-else-if="item.status === 'closed'"
                      color="info"
                      small
                      text
                      @click="viewBatchReport(item)"
                    >
                      <v-icon left small>mdi-eye</v-icon>
                      Ver
                    </v-btn>
                    <v-btn icon small @click="deleteBatch(item)">
                      <v-icon small color="error">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>

                <!-- Resumen de Lotes -->
                <v-card outlined class="mt-4 pa-4">
                  <h4 class="text-subtitle-1 mb-3">Resumen de Todos los Lotes</h4>
                  <v-row dense>
                    <v-col cols="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold">{{ totalBatchItems }}</div>
                        <div class="text-caption grey--text">Items Totales</div>
                      </div>
                    </v-col>
                    <v-col cols="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold success--text">
                          ${{ formatCurrency(totalBatchesCost) }}
                        </div>
                        <div class="text-caption grey--text">Costo Total</div>
                      </div>
                    </v-col>
                    <v-col cols="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold info--text">
                          ${{ formatCurrency(totalBatchesPrice) }}
                        </div>
                        <div class="text-caption grey--text">Precio Total</div>
                      </div>
                    </v-col>
                    <v-col cols="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold warning--text">
                          ${{ formatCurrency(totalBatchesMargin) }}
                        </div>
                        <div class="text-caption grey--text">Margen Total</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- Tab 3: Conteo -->
        <v-tab-item>
          <v-card-text>
            <!-- Resumen del Lote Actual -->
            <v-row v-if="currentBatch" class="mb-4">
              <v-col cols="12">
                <v-card outlined class="pa-4">
                  <h3 class="text-h6 mb-3">
                    <v-icon left>mdi-folder</v-icon>
                    {{ currentBatch.name }} - Resumen Actual
                  </h3>
                  <v-row dense>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold primary--text">
                          {{ currentBatch.itemCount || 0 }}
                        </div>
                        <div class="text-caption grey--text">Items Escaneados</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold success--text">
                          ${{ formatCurrency(getBatchTotalCost(currentBatch)) }}
                        </div>
                        <div class="text-caption grey--text">Costo Escaneado</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold info--text">
                          ${{ formatCurrency(getBatchTotalPrice(currentBatch)) }}
                        </div>
                        <div class="text-caption grey--text">Precio Escaneado</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold warning--text">
                          ${{ formatCurrency(getBatchTotalMargin(currentBatch)) }}
                        </div>
                        <div class="text-caption grey--text">Margen Escaneado</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <audit-scanner
              v-if="currentBatch"
              :batch="currentBatch"
              :inventory="localInventory"
              @item-scanned="onItemScanned"
              @batch-closed="onBatchClosed"
            />
          </v-card-text>
        </v-tab-item>

        <!-- Tab 4: Resultados -->
        <v-tab-item>
          <v-card-text>
            <!-- Resumen de Resultados -->
            <v-row class="mb-4">
              <v-col cols="12">
                <v-card outlined class="pa-4">
                  <h3 class="text-h6 mb-3">
                    <v-icon left>mdi-chart-box</v-icon>
                    Resumen General de Auditoría
                  </h3>
                  <v-row dense>
                    <v-col cols="6" md="2">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold primary--text">
                          {{ batches.length }}
                        </div>
                        <div class="text-caption grey--text">Lotes Totales</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="2">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold">
                          {{ totalBatchItems }}
                        </div>
                        <div class="text-caption grey--text">Items Auditados</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="2">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold success--text">
                          ${{ formatCurrency(totalBatchesCost) }}
                        </div>
                        <div class="text-caption grey--text">Costo Auditado</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="2">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold info--text">
                          ${{ formatCurrency(totalBatchesPrice) }}
                        </div>
                        <div class="text-caption grey--text">Precio Auditado</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="2">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold warning--text">
                          ${{ formatCurrency(totalBatchesMargin) }}
                        </div>
                        <div class="text-caption grey--text">Margen Auditado</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="2">
                      <div class="text-center">
                        <div class="text-h6 font-weight-bold" :class="auditCoverageColor">
                          {{ auditCoveragePercentage }}%
                        </div>
                        <div class="text-caption grey--text">Cobertura</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <audit-results
              :batches="batches"
              :inventory="localInventory"
              :audits="allAudits"
              :audit-d-b="auditDB"
            />
          </v-card-text>
        </v-tab-item>

        <!-- Tab 5: Cierre dinámico -->
        <v-tab-item>
          <v-card-text>
            <audit-closure
              :batches="batches"
              :inventory="localInventory"
              :audit-d-b="auditDB"
              @closure-completed="onClosureCompleted"
            />
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Diálogos y Notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      bottom
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>

    <!-- Diálogo de Confirmación -->
    <v-dialog v-model="confirmDialog.show" max-width="400">
      <v-card>
        <v-card-title>{{ confirmDialog.title }}</v-card-title>
        <v-card-text>{{ confirmDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmDialog.action"> Confirmar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver, webserverAsync } from "@/services/webserver.js";
import { isNetworkOnline } from "@/services/networkMonitor";
import AuditScanner from "./components/AuditScanner.vue";
import AuditResults from "./components/AuditResults.vue";
import AuditClosure from "./components/AuditClosure.vue";
import { AuditDatabase } from "./utils/auditDatabase.js";

export default {
  name: "AuditoriaInventario",

  components: {
    AuditScanner,
    AuditResults,
    AuditClosure,
  },

  data() {
    return {
      // Estado principal
      activeTab: 0,
      auditDB: null,

      // Estado de sincronización
      downloading: false,
      uploading: false,
      lastSync: null,

      // Progreso de descarga paginada del maestro
      downloadProgress: {
        loaded: 0,
        total: 0,
        percent: 0,
        page: 0,
        pages: 0,
        attempt: 0,
      },
      // idle | counting | downloading | saving | finalizing | done | paused | error
      downloadPhase: "idle",
      downloadStartedAt: null,
      downloadElapsed: 0,
      cancelRequested: false,
      pendingCheckpoint: null,

      // Inventario local. Se guarda CONGELADO (Object.freeze) para que Vue 2 no
      // haga observación profunda de decenas de miles de objetos (causa del freeze).
      localInventory: [],
      // Totales precalculados una sola vez, para no recorrer el array en cada computed.
      inventoryStats: { count: 0, cost: 0, price: 0 },

      // Gestión de lotes
      batches: [],
      currentBatch: null,
      newBatch: {
        name: "",
        description: "",
      },
      batchFormValid: false,

      // Headers de tabla
      batchHeaders: [
        { text: "Lote", value: "name" },
        { text: "Descripción", value: "description" },
        { text: "Estado", value: "status", align: "center" },
        { text: "Items", value: "itemCount", align: "center" },
        { text: "Costo Total", value: "totalCost", align: "center" },
        { text: "Precio Total", value: "totalPrice", align: "center" },
        { text: "Creado", value: "createdAt" },
        { text: "Acciones", value: "actions", sortable: false, align: "center" },
      ],

      // UI
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        action: () => {},
      },
    };
  },

  computed: {
    syncStatus() {
      if (this.downloading) {
        return {
          type: "info",
          icon: "mdi-sync",
          message: "Descargando inventario...",
        };
      }
      if (this.uploading) {
        return {
          type: "info",
          icon: "mdi-cloud-upload",
          message: "Sincronizando resultados...",
        };
      }
      if (!this.hasLocalInventory) {
        return {
          type: "warning",
          icon: "mdi-alert",
          message: "Sin datos locales. Descarga el inventario para comenzar.",
        };
      }
      return {
        type: "success",
        icon: "mdi-check-circle",
        message: "Listo para auditar",
      };
    },

    hasLocalInventory() {
      return this.localInventory.length > 0;
    },

    hasActiveBatch() {
      return this.batches.some((b) => b.status === "open");
    },

    hasAuditData() {
      return this.batches.some((b) => b.itemCount > 0);
    },

    canCloseAudit() {
      return this.batches.some(
        (b) => b.status === "open" && (b.itemCount || 0) > 0
      );
    },

    localInventoryCount() {
      return this.inventoryStats.count;
    },

    pendingSyncCount() {
      return this.batches.reduce((sum, batch) => sum + (batch.itemCount || 0), 0);
    },

    estimatedSize() {
      // Estimación basada en ~1KB por producto
      return Math.round(this.localInventoryCount / 1000) || 5;
    },

    /** Texto explicativo de la fase actual de la descarga. */
    downloadStatusText() {
      const p = this.downloadProgress;
      const fmt = (n) => Number(n || 0).toLocaleString();

      if (p.attempt > 1) {
        return `Reintentando página ${p.page} (intento ${p.attempt} de 3)…`;
      }
      switch (this.downloadPhase) {
        case "counting":
          return "Contando productos del catálogo…";
        case "downloading":
          return `Descargando página ${p.page} de ${p.pages} · ${fmt(
            p.loaded
          )} de ${fmt(p.total)} productos`;
        case "saving":
          return `Guardando en el dispositivo · ${fmt(p.loaded)} de ${fmt(
            p.total
          )} productos`;
        case "finalizing":
          return "Finalizando…";
        case "done":
          return `Completado · ${fmt(p.loaded)} productos`;
        case "paused":
          return `Pausada en ${fmt(p.loaded)} de ${fmt(p.total)} productos`;
        case "error":
          return `Interrumpida en ${fmt(p.loaded)} de ${fmt(p.total)} productos`;
        default:
          return "";
      }
    },

    /** Etiqueta del botón principal según haya o no descarga pendiente. */
    downloadButtonLabel() {
      if (this.pendingCheckpoint && this.pendingCheckpoint.total) {
        const loaded = this.downloadProgress.loaded || this.localInventoryCount;
        return `Reanudar descarga (${Number(
          loaded
        ).toLocaleString()} de ${Number(
          this.pendingCheckpoint.total
        ).toLocaleString()})`;
      }
      return "Descargar Inventario Actual";
    },

    lastSyncFormatted() {
      if (!this.lastSync) return "Nunca";
      return new Date(this.lastSync).toLocaleString("es-ES");
    },

    allAudits() {
      // Combinar todas las auditorías de todos los lotes
      return this.batches.reduce((audits, batch) => {
        return audits.concat(batch.audits || []);
      }, []);
    },

    // Valores monetarios: se leen de los totales precalculados (ver
    // computeInventoryStats) en vez de recorrer el inventario completo en cada
    // recálculo, que con decenas de miles de productos bloqueaba la UI.
    totalInventoryCost() {
      return this.inventoryStats.cost;
    },

    totalInventoryPrice() {
      return this.inventoryStats.price;
    },

    totalInventoryMargin() {
      return this.totalInventoryPrice - this.totalInventoryCost;
    },

    totalBatchItems() {
      return this.batches.reduce((sum, batch) => sum + (batch.itemCount || 0), 0);
    },

    totalBatchesCost() {
      return this.batches.reduce((sum, batch) => sum + this.getBatchTotalCost(batch), 0);
    },

    totalBatchesPrice() {
      return this.batches.reduce((sum, batch) => sum + this.getBatchTotalPrice(batch), 0);
    },

    totalBatchesMargin() {
      return this.totalBatchesPrice - this.totalBatchesCost;
    },

    auditCoveragePercentage() {
      if (this.localInventoryCount === 0) return 0;
      return Math.round((this.totalBatchItems / this.localInventoryCount) * 100);
    },

    auditCoverageColor() {
      const percentage = this.auditCoveragePercentage;
      if (percentage >= 80) return "success--text";
      if (percentage >= 50) return "warning--text";
      return "error--text";
    },

    auditKpis() {
      const totalBatches = this.batches.length;
      const openBatches = this.batches.filter((b) => b.status === "open").length;
      const completedBatches = this.batches.filter((b) => b.status === "closed").length;
      const totalScanned = this.allAudits.length;

      return [
        {
          key: "inventory",
          label: "Productos",
          value: this.localInventoryCount.toLocaleString(),
          subtitle: "En sistema",
          icon: "mdi-package-variant",
          color: "primary",
        },
        {
          key: "batches",
          label: "Lotes",
          value: totalBatches,
          subtitle: `${openBatches} abiertos`,
          icon: "mdi-folder-multiple",
          color: "info",
        },
        {
          key: "scanned",
          label: "Escaneados",
          value: totalScanned,
          subtitle: `${completedBatches} completados`,
          icon: "mdi-barcode-scan",
          color: "success",
        },
        {
          key: "cost",
          label: "Costo Total",
          value: `$${this.formatCurrency(this.totalInventoryCost)}`,
          subtitle: "Inventario",
          icon: "mdi-currency-usd",
          color: "success",
        },
        {
          key: "price",
          label: "Precio Total",
          value: `$${this.formatCurrency(this.totalInventoryPrice)}`,
          subtitle: "Inventario",
          icon: "mdi-tag-multiple",
          color: "info",
        },
        {
          key: "pending",
          label: "Pendientes",
          value: this.pendingSyncCount,
          subtitle: "Por sincronizar",
          icon: "mdi-cloud-upload",
          color: this.pendingSyncCount > 0 ? "warning" : "success",
        },
      ];
    },
  },

  async mounted() {
    // Inicializar base de datos local
    this.auditDB = new AuditDatabase();
    await this.auditDB.init();

    // Cargar datos locales
    await this.loadLocalData();
  },

  methods: {
    // Sincronización
    /** Cede el hilo principal para que Vue repinte (barra de progreso fluida). */
    yieldToMain() {
      return new Promise((resolve) => setTimeout(resolve, 0));
    },

    /** Recorre el inventario UNA vez y devuelve los totales precalculados. */
    computeInventoryStats(list) {
      let cost = 0;
      let price = 0;
      const items = list || [];
      for (const item of items) {
        const stock = parseFloat(item.cantidad || 0);
        cost += parseFloat(item.costo || 0) * stock;
        price += parseFloat(item.precio || 0) * stock;
      }
      return { count: items.length, cost, price };
    },

    /** Solicita cancelar la descarga en curso (se detiene al terminar la página). */
    requestCancelDownload() {
      this.cancelRequested = true;
      this.showNotification("Cancelando al terminar la página actual…", "info");
    },

    /**
     * Descarga una página con reintentos y backoff. Si no hay red, espera y
     * reintenta en lugar de abortar toda la descarga.
     */
    async fetchPageWithRetry(baseQry, page, pageSize, maxAttempts = 3) {
      const backoff = [1000, 3000, 6000];
      let lastError = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        this.downloadProgress.attempt = attempt;
        if (attempt > 1) {
          await new Promise((r) => setTimeout(r, backoff[attempt - 2] || 6000));
        }
        if (!isNetworkOnline()) {
          lastError = new Error("Sin conexión a internet");
          continue;
        }
        try {
          const chunk = await webserverAsync("get_catalog", {
            ...baseQry,
            page,
            pageSize,
          });
          if (!Array.isArray(chunk)) throw new Error("Datos inválidos recibidos");
          this.downloadProgress.attempt = 0;
          return chunk;
        } catch (error) {
          lastError = error;
          console.warn(`Página ${page} falló (intento ${attempt}):`, error.message);
        }
      }
      throw lastError || new Error("No se pudo descargar la página");
    },

    async downloadInventory() {
      // Chunk moderado: menos trabajo síncrono por página y progreso más fluido.
      const PAGE_SIZE = 250;
      const storeKey = window.store.store_id;

      this.downloading = true;
      this.cancelRequested = false;
      this.downloadPhase = "counting";
      this.downloadStartedAt = Date.now();
      this.downloadElapsed = 0;
      this.downloadProgress = {
        loaded: 0,
        total: 0,
        percent: 0,
        page: 0,
        pages: 0,
        attempt: 0,
      };

      const elapsedTimer = setInterval(() => {
        this.downloadElapsed = Math.round(
          (Date.now() - this.downloadStartedAt) / 1000
        );
      }, 1000);

      const filters = [
        {
          field: "id_pos",
          operator: "=",
          // Catálogo maestro = id_pos de 6 dígitos (cuenta/cloud), no el
          // store_id completo de 9 díg. (que es el inventario por tienda,
          // aún inexistente en sistematización). Mismo patrón que Master.vue.
          value: storeKey.substring(0, 6),
        },
      ];

      const baseQry = {
        store: storeKey,
        table: "items",
        filters: filters,
        // El catálogo cloud (id_pos 6 díg.) trae cantidad=1 (placeholder). Con
        // storeStock pedimos el stock REAL de la tienda (id_pos 9 díg.) para que
        // la columna "Sistema" del escáner muestre las existencias correctas.
        storeStock: true,
      };

      let all = [];
      let startPage = 0;

      try {
        // 1) Total real para el progreso
        const countRes = await webserverAsync("get_catalog", {
          ...baseQry,
          countOnly: true,
        });
        const total =
          Array.isArray(countRes) && countRes[0]
            ? Number(countRes[0].total) || 0
            : 0;
        this.downloadProgress.total = total;
        this.downloadProgress.pages = Math.ceil(total / PAGE_SIZE) || 0;

        // 2) ¿Reanudar o empezar limpio?
        const checkpoint = await this.auditDB.getDownloadCheckpoint();
        const canResume =
          checkpoint &&
          checkpoint.storeKey === storeKey &&
          Number(checkpoint.total) === total &&
          Number(checkpoint.page) > 0;

        if (canResume) {
          startPage = Number(checkpoint.page);
          // Conservamos lo ya guardado en IndexedDB y seguimos desde ahí.
          all = (await this.auditDB.getInventory()) || [];
          this.downloadProgress.loaded = all.length;
          this.downloadProgress.percent = total
            ? Math.round((all.length / total) * 100)
            : 0;
          this.showNotification(
            `Reanudando descarga desde ${all.length} de ${total} productos`,
            "info"
          );
        } else {
          await this.auditDB.clearInventory();
          await this.auditDB.clearDownloadCheckpoint();
        }

        // 3) Descarga paginada, guardando cada chunk de forma incremental
        this.downloadPhase = "downloading";
        let cancelled = false;

        for (let page = startPage; page * PAGE_SIZE < total; page++) {
          if (this.cancelRequested) {
            cancelled = true;
            break;
          }

          this.downloadProgress.page = page + 1;
          const chunk = await this.fetchPageWithRetry(baseQry, page, PAGE_SIZE);

          const isLast =
            (page + 1) * PAGE_SIZE >= total || chunk.length < PAGE_SIZE;

          this.downloadPhase = "saving";
          await this.auditDB.appendInventoryChunk(chunk, isLast);
          all.push(...chunk);

          // Punto de control: página siguiente por descargar
          await this.auditDB.saveDownloadCheckpoint({
            page: page + 1,
            total,
            storeKey,
          });

          this.downloadProgress.loaded = all.length;
          this.downloadProgress.percent = total
            ? Math.round((all.length / total) * 100)
            : 0;
          this.downloadPhase = "downloading";

          // Ceder el hilo para que la barra se repinte de verdad
          await this.yieldToMain();

          // Salvaguarda: fin de datos antes de lo esperado
          if (chunk.length < PAGE_SIZE) break;
        }

        if (cancelled) {
          this.downloadPhase = "paused";
          this.pendingCheckpoint = await this.auditDB.getDownloadCheckpoint();
          this.showNotification(
            `Descarga pausada: ${all.length} de ${total} productos. Puedes reanudarla.`,
            "warning"
          );
          return;
        }

        // 4) Volcado final (congelado para no disparar la reactividad profunda)
        this.downloadPhase = "finalizing";
        await this.yieldToMain();

        this.inventoryStats = this.computeInventoryStats(all);
        this.localInventory = Object.freeze(all);
        this.lastSync = new Date();

        await this.auditDB.clearDownloadCheckpoint();
        this.pendingCheckpoint = null;
        this.downloadPhase = "done";

        await this.yieldToMain();

        this.showNotification(
          `${all.length} productos descargados exitosamente`,
          "success"
        );

        // Ir al tab de lotes
        this.activeTab = 1;
      } catch (error) {
        console.error("Error descargando inventario:", error);
        this.downloadPhase = "error";
        this.pendingCheckpoint = await this.auditDB
          .getDownloadCheckpoint()
          .catch(() => null);
        this.showNotification(
          `Error al descargar (${this.downloadProgress.loaded} de ${this.downloadProgress.total}). Pulsa Reanudar para continuar.`,
          "error"
        );
      } finally {
        clearInterval(elapsedTimer);
        this.downloading = false;
        this.cancelRequested = false;
        this.downloadProgress.attempt = 0;
      }
    },

    async uploadResults() {
      this.uploading = true;

      try {
        // Preparar datos para enviar
        const auditData = {
          store: window.store.store_id,
          userId: window.userId,
          batches: this.batches.filter((b) => b.itemCount > 0),
          timestamp: new Date(),
        };

        webserver("save_audit_results", auditData, async (response) => {
          if (response.success) {
            // Marcar como sincronizados
            await this.auditDB.markAsSynced(this.batches);

            this.showNotification("Resultados sincronizados exitosamente", "success");

            // Limpiar datos locales después de sincronizar
            if (confirm("¿Desea limpiar los datos locales después de sincronizar?")) {
              await this.clearLocalData();
            }
          } else {
            throw new Error("Error en la sincronización");
          }

          this.uploading = false;
        });
      } catch (error) {
        console.error("Error subiendo resultados:", error);
        this.showNotification("Error al sincronizar resultados", "error");
        this.uploading = false;
      }
    },

    // Gestión de Lotes
    async createBatch() {
      if (this.$refs.batchForm.validate()) {
        const batch = {
          id: Date.now().toString(),
          name: this.newBatch.name,
          description: this.newBatch.description,
          status: "open",
          createdAt: new Date(),
          closedAt: null,
          userId: window.userId,
          itemCount: 0,
          audits: [],
        };

        // Guardar en IndexedDB
        await this.auditDB.saveBatch(batch);
        this.batches.push(batch);

        // Limpiar formulario
        this.newBatch = { name: "", description: "" };
        this.$refs.batchForm.reset();

        this.showNotification("Lote creado exitosamente", "success");

        // Seleccionar automáticamente el nuevo lote
        this.selectBatch(batch);
      }
    },

    selectBatch(batch) {
      this.currentBatch = batch;
      this.activeTab = 2; // Ir al tab de conteo
    },

    async deleteBatch(batch) {
      this.confirmDialog = {
        show: true,
        title: "Eliminar Lote",
        message: `¿Está seguro de eliminar el lote "${batch.name}"? Esta acción no se puede deshacer.`,
        action: async () => {
          await this.auditDB.deleteBatch(batch.id);
          const index = this.batches.findIndex((b) => b.id === batch.id);
          if (index > -1) {
            this.batches.splice(index, 1);
          }
          this.confirmDialog.show = false;
          this.showNotification("Lote eliminado", "info");
        },
      };
    },

    viewBatchReport(batch) {
      // Implementar vista detallada del lote
      console.log("Ver reporte del lote:", batch);
    },

    // Eventos del scanner
    async onItemScanned(scanData) {
      // Buscar el lote actual
      const batch = this.batches.find((b) => b.id === scanData.batchId);
      if (batch) {
        if (!batch.audits) batch.audits = [];

        if (scanData.deleted) {
          // Eliminar la auditoría
          await this.auditDB.deleteAudit(scanData.batchId, scanData.productId);

          // Actualizar el array local
          const auditIndex = batch.audits.findIndex(
            (a) => a.productId === scanData.productId
          );
          if (auditIndex > -1) {
            batch.audits.splice(auditIndex, 1);
          }
        } else {
          // Preparar datos de auditoría
          const auditData = {
            batchId: scanData.batchId,
            productId: scanData.productId,
            productName: scanData.productName,
            productRef: scanData.productRef,
            productCost: scanData.productCost,
            productPrice: scanData.productPrice,
            systemQuantity: scanData.systemQuantity,
            userId: scanData.userId,
          };

          if (scanData.isEdit) {
            // Si es una edición, establecer la cantidad total directamente
            auditData.countedQuantity = scanData.countedQuantity;
            const updatedAudit = await this.auditDB.updateAudit(auditData);

            // Actualizar el array local
            const existingIndex = batch.audits.findIndex(
              (a) => a.productId === scanData.productId
            );
            if (existingIndex > -1) {
              // Forzar reactividad usando Vue.set para que Vue detecte el cambio
              this.$set(batch.audits, existingIndex, updatedAudit);
            }
          } else {
            // Si no es edición, usar la lógica de agregar cantidad
            auditData.quantityToAdd =
              scanData.quantityToAdd || scanData.countedQuantity || 1;

            // Guardar o actualizar en la base de datos
            const updatedAudit = await this.auditDB.saveOrUpdateAudit(auditData);

            // Actualizar el array local
            const existingIndex = batch.audits.findIndex(
              (a) => a.productId === scanData.productId
            );
            if (existingIndex > -1) {
              // Forzar reactividad usando Vue.set o splice para que Vue detecte el cambio
              this.$set(batch.audits, existingIndex, updatedAudit);
            } else {
              batch.audits.push(updatedAudit);
            }
          }
        }

        // Actualizar conteo de items y forzar reactividad
        batch.itemCount = batch.audits.length;

        // Forzar reactividad del lote completo para asegurar que se actualice la vista
        const batchIndex = this.batches.findIndex((b) => b.id === batch.id);
        if (batchIndex > -1) {
          this.$set(this.batches, batchIndex, { ...batch });
        }

        // Guardar en IndexedDB
        await this.auditDB.updateBatch(batch);
      }
    },

    async onBatchClosed(batchId) {
      const batch = this.batches.find((b) => b.id === batchId);
      if (batch) {
        batch.status = "closed";
        batch.closedAt = new Date();
        await this.auditDB.updateBatch(batch);

        this.showNotification("Lote cerrado exitosamente", "success");
        this.currentBatch = null;
        this.activeTab = 1; // Volver a gestión de lotes
      }
    },

    // Utilidades
    async loadLocalData() {
      try {
        const inventory = (await this.auditDB.getInventory()) || [];
        // Congelar evita que Vue 2 haga reactivos miles de objetos (freeze de la UI).
        this.localInventory = Object.freeze(inventory);
        this.inventoryStats = this.computeInventoryStats(inventory);
        this.pendingCheckpoint = await this.auditDB.getDownloadCheckpoint();
        this.batches = (await this.auditDB.getBatches()) || [];

        // Cargar las auditorías para cada lote
        for (const batch of this.batches) {
          batch.audits = (await this.auditDB.getAuditsByBatch(batch.id)) || [];
          batch.itemCount = batch.audits.length;
        }

        this.lastSync = await this.auditDB.getLastSync();
      } catch (error) {
        console.error("Error cargando datos locales:", error);
      }
    },

    async clearLocalData() {
      this.confirmDialog = {
        show: true,
        title: "Limpiar Datos Locales",
        message:
          "¿Está seguro de eliminar todos los datos locales? Esta acción no se puede deshacer.",
        action: async () => {
          await this.auditDB.clearAll();
          await this.auditDB.clearDownloadCheckpoint();
          this.localInventory = [];
          this.inventoryStats = { count: 0, cost: 0, price: 0 };
          this.pendingCheckpoint = null;
          this.downloadPhase = "idle";
          this.batches = [];
          this.currentBatch = null;
          this.lastSync = null;
          this.activeTab = 0;
          this.confirmDialog.show = false;
          this.showNotification("Datos locales eliminados", "info");
        },
      };
    },

    getBatchStatusColor(status) {
      if (status === "open") return "success";
      if (status === "audited") return "info";
      return "grey";
    },

    getBatchStatusText(status) {
      if (status === "open") return "Abierto";
      if (status === "audited") return "Auditado";
      return "Cerrado";
    },

    async onClosureCompleted(closure) {
      await this.loadLocalData();
      const refs = (closure.adjustmentRefs || [])
        .filter((r) => r.doc_number)
        .map((r) => `${r.type}:${r.doc_number}`)
        .join(" · ");
      this.showNotification(
        "Cierre " + closure.id + (refs ? " — " + refs : ""),
        "success"
      );
      this.activeTab = 1;
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    showNotification(text, color = "success") {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout: 3000,
      };
    },

    // Nuevos métodos para cálculos de lotes
    getBatchTotalCost(batch) {
      if (!batch.audits || !Array.isArray(batch.audits)) return 0;

      return batch.audits.reduce((sum, audit) => {
        const cost = parseFloat(audit.productCost || audit.costo || 0);
        const quantity = parseFloat(audit.countedQuantity || audit.cantidad || 0);
        return sum + cost * quantity;
      }, 0);
    },

    getBatchTotalPrice(batch) {
      if (!batch.audits || !Array.isArray(batch.audits)) return 0;

      return batch.audits.reduce((sum, audit) => {
        const price = parseFloat(audit.productPrice || audit.precio || 0);
        const quantity = parseFloat(audit.countedQuantity || audit.cantidad || 0);
        return sum + price * quantity;
      }, 0);
    },

    getBatchTotalMargin(batch) {
      return this.getBatchTotalPrice(batch) - this.getBatchTotalCost(batch);
    },

    formatCurrency(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return parseFloat(value).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
/* Estilos específicos del componente */
</style>
